/**
 * Best-effort match of free-text lender/company name (e.g. "South State") to a
 * LENDERS record (e.g. "South State Bank"). Tolerant on purpose: exact match
 * first, then one name being a prefix of / contained in the other, preferring
 * the shortest candidate name to reduce ambiguity. Returns the record or null.
 * `contains: false` drops the containment rules, which on a short list match "Synovus Bank" to "US Bank".
 */
export function matchLenderByName(lenders, text, { contains = true } = {}) {
    const norm = (s) => String(s == null ? '' : s).toLowerCase().replace(/\s+/g, ' ').trim();
    // Alphanumeric-only form so spacing/punctuation is ignored entirely
    // ("SouthState" and "South State" both squash to "southstate").
    const squash = (s) => norm(s).replace(/[^a-z0-9]/g, '');
    const q = norm(text);
    if (!q || !Array.isArray(lenders)) return null;
    const qs = squash(text);

    const pick = (key, pred) =>
        lenders
            .filter((l) => l && pred(key(l.NAME)))
            .sort((a, b) => key(a.NAME).length - key(b.NAME).length)[0];

    return (
        pick(norm, (n) => n === q) ||
        pick(norm, (n) => n.startsWith(q)) ||
        pick(norm, (n) => q.startsWith(n)) ||
        (contains && pick(norm, (n) => n.includes(q))) ||
        (contains && pick(norm, (n) => q.includes(n))) ||
        pick(squash, (n) => !!n && n === qs) ||
        pick(squash, (n) => !!n && n.startsWith(qs)) ||
        pick(squash, (n) => !!n && qs.startsWith(n)) ||
        (contains && pick(squash, (n) => !!n && n.includes(qs))) ||
        (contains && pick(squash, (n) => !!n && qs.includes(n))) ||
        null
    );
}
